import * as React from "react";
import { useEffect } from "react";

import { MegamenuLink } from "./MegamenuLink";

export const MegamenuLinks = ({ settings, megamenuSM, level = null }: any) => {
    const layout = settings.section[`level${level}_layout`] || 'default';
    const { firstLevelLinks, secondLevelLinks, hoveredLink } = megamenuSM;
    const { show_secondlevel_open_always, show_thirdlevel_open_always, megamenu_trigger } = settings.section;

    // Determines the initial level to render based on the level prop
    const getInitialLinks = () => {
        if (level === 2) return secondLevelLinks;
        if (level === 3 && hoveredLink?.links) return hoveredLink.links;
        return firstLevelLinks;
    };

    // Get level-specific styling
    const getLevelContainer = (currentLevel) => {
        const levelStyles = {
            backgroundColor: settings.section[`level${currentLevel}_bg_color`] || 'transparent',
            borderWidth: settings.section[`level${currentLevel}_border_width`] || 0,
            borderStyle: settings.section[`level${currentLevel}_border_style`] || 'solid',
            borderColor: settings.section[`level${currentLevel}_border_color`] || '#e2e8f0',
            borderRadius: settings.section[`level${currentLevel}_border_radius`] ? 
                `${settings.section[`level${currentLevel}_border_radius`]}px` : '0',
            padding: settings.section[`level${currentLevel}_padding`] ? 
                `${settings.section[`level${currentLevel}_padding`]}px` : '0',
            margin: settings.section[`level${currentLevel}_margin`] || '0',
            width: settings.section[`level${currentLevel}_width`] ? 
                `${settings.section[`level${currentLevel}_width`]}px` : 'auto',
            minHeight: settings.section[`level${currentLevel}_min_height`] ?
                `${settings.section[`level${currentLevel}_min_height`]}px` : 'auto',
            maxHeight: settings.section[`level${currentLevel}_max_height`] ? 
                `${settings.section[`level${currentLevel}_max_height`]}px` : 'none',
            overflow: settings.section[`level${currentLevel}_max_height`] ? 'auto' : 'visible',
            gap: settings.section[`level${currentLevel}_link_spacing`] ? 
                `${settings.section[`level${currentLevel}_link_spacing`]}px` : '0',
        };
        
        // Only add border if width > 0
        const styles = {};
        for (const key in levelStyles) {
            if (levelStyles[key] && levelStyles[key] !== '0' && levelStyles[key] !== 'none' && 
                levelStyles[key] !== 'transparent' && levelStyles[key] !== 'auto' && 
                !(key === 'borderWidth' && levelStyles[key] === 0)) {
                styles[key] = levelStyles[key];
            }
        }
        
        return styles;
    };

    // Get styling for link items within this level
    const getLinkItemStyles = (currentLevel) => {
        return {
            paddingBottom: settings.section[`level${currentLevel}_link_padding`] ? 
                `${settings.section[`level${currentLevel}_link_padding`]}px` : '0',
            borderBottom: settings.section[`level${currentLevel}_link_dividers`] ? 
                `1px solid ${settings.section[`level${currentLevel}_divider_color`] || '#e2e8f0'}` : 'none',
        };
    };

   
 
    // Recursive function to render links at any level
    const renderLinks = (links, currentLevel) => {
        if (!links) return null;

        const containerStyles = getLevelContainer(currentLevel);
        const linkItemStyles = getLinkItemStyles(currentLevel);
        const menuIcons = {}
        settings.section[`level${currentLevel}_menu_icons`]
            ?.split(',')
            ?.forEach(item => {
                const [menu, icon] = item.split('|');
                menuIcons[menu?.trim()] = icon?.trim();
            });

        return links.map((subItem, index) => {
            // Consider a link active if it's hovered OR it's the first item when nothing is hovered
            const hasSubLinks = subItem.links && subItem.links.length > 0;
            const shouldRenderSubLinks = (currentLevel === 2 && (show_secondlevel_open_always
                || hoveredLink?.handle === subItem.handle))
                || (currentLevel === 2 && show_thirdlevel_open_always);
            
            const initActive = (index === 0 || hoveredLink?.handle === subItem.handle )&& currentLevel === 1 && hasSubLinks;
    

            return (
                <React.Fragment key={`${subItem.handle}-${index}`}>
                    <div className={`layout-${layout} level-${currentLevel}`}>
                        <ul
                            className={`megamenu__link-container  ${initActive ? 'active' : ''} my-[${settings.section.gap_between_text_y}px]`}
                            style={linkItemStyles}
                            key={`${hoveredLink}-${subItem.handle}`}
                            data-link-id={subItem.handle}
                            onMouseEnter={() => layout === 'default' && megamenuSM.onLinkHover(subItem)}
                            onClick={() => currentLevel === 1 && megamenuSM.handleFirstLevelClick(subItem)}
                        >
                            <MegamenuLink
                                icon={menuIcons[subItem.title]}
                                settings={settings}
                                hasSubLinks={hasSubLinks}
                                subItem={subItem}
                                level={currentLevel}
                                onLinkClick={() => currentLevel === 1 && megamenuSM.handleFirstLevelClick(subItem)}
                            />
                        </ul>

                        {shouldRenderSubLinks && hasSubLinks && layout === 'grid' && (
                            <div className={`megamenu-sublinks level-${currentLevel + 1}`}>
                                {renderLinks(subItem.links, currentLevel + 1)}
                            </div>
                        )}
                    </div>

                    {shouldRenderSubLinks && hasSubLinks && layout === 'default' && (
                        <div className={`megamenu-sublinks level-${currentLevel + 1}`}>
                            {renderLinks(subItem.links, currentLevel + 1)}
                        </div>
                    )}
                </React.Fragment>
            );
        });
    };

    const currentLevel = level || 1;
    const containerStyles = getLevelContainer(currentLevel);

    return (
        <div
            className={`
                megamenu-links-container
                megamenu-level-${currentLevel}
                ${layout === 'grid' ? `flex gap-4` : 'flex flex-col'}
            `}
            style={containerStyles}
        >
            {renderLinks(getInitialLinks(), currentLevel)}
        </div>
    );
};
